import { useEffect, useState } from "react";
import type { AdminQuestion } from "../../types";
import { permissionLabel, nameTypeLabel } from "./StatusBadge";
import { formatDateTime } from "../../lib/format";

const HUBSPOT_PORTAL_ID = import.meta.env.VITE_HUBSPOT_PORTAL_ID as
  | string
  | undefined;

export interface QuestionDetailHandlers {
  onSaveEdits: (fields: {
    display_text: string;
    display_name: string;
    operator_memo: string;
  }) => Promise<void>;
  onApprove: () => Promise<void>;
  onDisplay: () => Promise<void>;
  onAnswered: () => Promise<void>;
  onHide: () => Promise<void>;
  onResync: () => Promise<void>;
}

export function QuestionDetail({
  question,
  busy,
  handlers,
}: {
  question: AdminQuestion;
  busy: boolean;
  handlers: QuestionDetailHandlers;
}) {
  const [displayText, setDisplayText] = useState(question.display_text ?? "");
  const [displayName, setDisplayName] = useState(question.display_name ?? "");
  const [memo, setMemo] = useState(question.operator_memo ?? "");

  // Reset local edit state when a different question is selected.
  useEffect(() => {
    setDisplayText(question.display_text ?? "");
    setDisplayName(question.display_name ?? "");
    setMemo(question.operator_memo ?? "");
  }, [question.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reg = question.registration;
  const cannotDisplay = question.display_permission === "do_not_display";
  const hubspotUrl =
    question.hubspot_contact_id && HUBSPOT_PORTAL_ID
      ? `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-1/${question.hubspot_contact_id}`
      : null;

  return (
    <div className="question-detail">
      <section>
        <h3>質問本文</h3>
        <p className="qd-question-text">{question.question_text}</p>
      </section>

      <section className="qd-meta">
        <h3>投稿者</h3>
        <p>
          {reg?.company_name ?? "-"} / {reg?.last_name ?? ""} {reg?.first_name ?? ""}
        </p>
        <p className="muted">{reg?.email ?? "-"}</p>
        <dl>
          <div>
            <dt>表示可否</dt>
            <dd>{permissionLabel(question.display_permission)}</dd>
          </div>
          <div>
            <dt>表示名タイプ</dt>
            <dd>{nameTypeLabel(question.display_name_type)}</dd>
          </div>
          <div>
            <dt>個別相談</dt>
            <dd>{question.wants_consultation ? "あり" : "なし"}</dd>
          </div>
          <div>
            <dt>視聴状況</dt>
            <dd>
              {question.watch_seconds ?? "-"}秒 / {question.watch_percent ?? "-"}%
            </dd>
          </div>
          <div>
            <dt>送信日時</dt>
            <dd>{formatDateTime(question.created_at)}</dd>
          </div>
          <div>
            <dt>HubSpot Note</dt>
            <dd>{question.hubspot_note_id ? "作成済み" : "未作成"}</dd>
          </div>
        </dl>
      </section>

      <section>
        <h3>表示文言編集</h3>
        <textarea
          value={displayText}
          onChange={(e) => setDisplayText(e.target.value)}
          rows={3}
          maxLength={1000}
        />
        <label className="field-label">表示名</label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={200}
        />
      </section>

      <section>
        <h3>運営メモ</h3>
        <textarea
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder="営業フォロー対象 など"
        />
        <button
          type="button"
          className="btn-secondary"
          disabled={busy}
          onClick={() =>
            handlers.onSaveEdits({
              display_text: displayText,
              display_name: displayName,
              operator_memo: memo,
            })
          }
        >
          編集内容を保存
        </button>
      </section>

      <section className="qd-actions">
        <h3>操作</h3>
        <div className="action-buttons">
          <button type="button" disabled={busy} onClick={handlers.onApprove}>
            承認
          </button>
          <button
            type="button"
            disabled={busy || cannotDisplay}
            title={cannotDisplay ? "この質問は表示不可です" : undefined}
            onClick={handlers.onDisplay}
          >
            {question.status === "displaying" ? "配信から外す" : "配信画面に表示"}
          </button>
          <button type="button" disabled={busy} onClick={handlers.onAnswered}>
            回答済み
          </button>
          <button type="button" disabled={busy} onClick={handlers.onHide}>
            非表示
          </button>
          {hubspotUrl ? (
            <a className="btn-link" href={hubspotUrl} target="_blank" rel="noreferrer">
              HubSpotを開く
            </a>
          ) : (
            <button type="button" disabled title="HubSpotコンタクト未連携">
              HubSpotを開く
            </button>
          )}
          <button type="button" disabled={busy} onClick={handlers.onResync}>
            再同期
          </button>
        </div>
        {cannotDisplay && (
          <p className="warn">この質問は「表示しないでほしい」が選択されています。</p>
        )}
      </section>
    </div>
  );
}
